/**
 * How to connect each assistant, as shown on Connect your AI. Steps are plain strings with two
 * marks: `{Menu > Item}` renders as interface chips and `**word**` as bold.
 */
export interface AssistantGuide {
  id: string
  label: string
  intro: string
  /** Opens the assistant itself. ChatGPT has none: its admin has to add SeedForge first. */
  open?: { label: string; href: string }
  /** The page's one orange action is "Copy request for my admin" instead of "Copy address". */
  adminRequest?: boolean
  /** The page's one orange action is "Ask us for set-up details": the firm's IT admin sets it up. */
  setupDetails?: boolean
  steps: string[]
  footer?: string
  /** A link after the footer: ChatGPT's waiting list, or asking us about Gemini Enterprise. */
  footerAction?: 'tell-me-when' | 'ask-us'
}

export const ASSISTANT_GUIDES: AssistantGuide[] = [
  {
    id: 'claude',
    label: 'Claude',
    intro: 'Works on every Claude plan. Free plans allow one custom connector.',
    open: { label: 'Open Claude', href: 'https://claude.ai' },
    steps: [
      'In Claude, open {Customize > Connectors > + Add > Add custom connector}.',
      'Name it SeedForge and paste the server address.',
      'Choose **Connect** and sign in with your SeedForge account.',
    ],
    footer: 'On Team or Enterprise, an owner adds SeedForge first. Then you choose Connect.',
  },
  {
    id: 'chatgpt',
    label: 'ChatGPT',
    intro: 'Works on ChatGPT Business, Enterprise and Edu, once your workspace admin adds it.',
    adminRequest: true,
    steps: [
      'Your admin turns on developer mode in {Workspace settings > Permissions & roles > Connected data}.',
      'Your admin adds SeedForge as a custom MCP server with the server address and publishes it to the workspace.',
      'You open SeedForge in ChatGPT, choose **Connect** and sign in with your SeedForge account.',
    ],
    footer: 'Other ChatGPT plans cannot add SeedForge yet.',
    footerAction: 'tell-me-when',
  },
  {
    id: 'gemini',
    label: 'Gemini',
    intro:
      'Works in the Gemini app on Google AI Pro and Ultra, with a personal account in the United States.',
    open: { label: 'Open Gemini', href: 'https://gemini.google.com' },
    steps: [
      'In Gemini, open {Settings & help > Connected apps}, then under Custom apps for Spark choose {Add a custom app}.',
      'Paste the server address and choose Next.',
      'Sign in with your SeedForge account.',
    ],
    footer:
      'Gemini Enterprise at work? Your IT admin connects SeedForge with set-up details from us.',
    footerAction: 'ask-us',
  },
  {
    id: 'copilot',
    label: 'Microsoft Copilot',
    intro: 'Microsoft 365 Copilot connects through your firm’s IT admin.',
    setupDetails: true,
    steps: [
      'Ask us for the set-up details. We send them to your IT admin.',
      'Once your admin has added SeedForge, choose **Connect** in Copilot and sign in with your SeedForge account.',
    ],
  },
  {
    id: 'perplexity',
    label: 'Perplexity',
    intro: 'Works on Perplexity Pro, Max and Enterprise.',
    open: { label: 'Open Perplexity', href: 'https://www.perplexity.ai' },
    steps: [
      'In Perplexity, open {Account settings > Connectors > + Custom connector > Remote}.',
      'Name it SeedForge, paste the server address, and choose OAuth and Streamable HTTP.',
      'Save and sign in with your SeedForge account.',
    ],
    footer: 'On Enterprise, an admin first allows members to add custom connectors.',
  },
  {
    id: 'lechat',
    label: 'Le Chat',
    intro: 'Works on every Le Chat plan, including Free.',
    open: { label: 'Open Le Chat', href: 'https://chat.mistral.ai' },
    steps: [
      'In Le Chat, open {Connectors > + Add Connector > Custom MCP Connector}.',
      'Name it seedforge and paste the server address.',
      'Choose **Connect** and sign in with your SeedForge account.',
    ],
    footer: 'On Team and Enterprise, an admin adds connectors for the workspace.',
  },
]

export const DEFAULT_ASSISTANT_ID = 'claude'
export const OTHER_ASSISTANTS_ID = 'other'

export const EXAMPLE_QUESTIONS = [
  'Find seed startups in Europe on SeedForge working on climate tech that report revenue.',
  'Analyse Kelpwise on SeedForge: team, traction, market and the main risks, with the founders’ dated lines.',
  'I meet Kelpwise’s founders next week. What should I ask, based on the gaps in what they published on SeedForge?',
]
